/**
 * SeasonBanner — Shows current season info + weekly challenge on home screen.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Trophy, Flame, Clock, ChevronRight } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { getActiveSeason, registerForSeason, isRegisteredForSeason, getSeasonParticipantCount, type Season } from '@/lib/seasons';
import { getCurrentChallenge, getWeekDates, type WeeklyChallenge } from '@/lib/weeklyChallenges';
import ChallengeSubmitModal from './ChallengeSubmitModal';

export default function SeasonBanner() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { session, profile } = useAuth();
  const [season, setSeason] = useState<Season | null>(null);
  const [challenge, setChallenge] = useState<WeeklyChallenge | null>(null);
  const [registered, setRegistered] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [session?.user?.id]);

  const loadData = async () => {
    try {
      const [s, ch] = await Promise.all([
        getActiveSeason(),
        getCurrentChallenge(),
      ]);
      setSeason(s);
      setChallenge(ch);

      if (s && session?.user?.id) {
        const [isReg, count] = await Promise.all([
          isRegisteredForSeason(s.id, session.user.id),
          getSeasonParticipantCount(s.id),
        ]);
        setRegistered(isReg);
        setParticipantCount(count);
      }
    } catch (err) {
      // Silently fail — banner just won't show
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!season || !session?.user?.id) return;
    setRegistering(true);
    const { success } = await registerForSeason(season.id, session.user.id);
    if (success) {
      setRegistered(true);
      setParticipantCount(prev => prev + 1);
    }
    setRegistering(false);
  };

  if (loading || !season) return null;

  const { start, end } = getWeekDates();
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const endDayName = end.toLocaleDateString('en-US', { weekday: 'short' });

  return (
    <View style={styles.container}>
      {/* Season Card */}
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => router.push('/season-standings')}
        style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
      >
        <View style={styles.seasonHeader}>
          <Trophy size={18} color={c.accent} />
          <Text style={[styles.seasonName, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
            {season.name.toUpperCase()}
          </Text>
        </View>

        <View style={styles.seasonMeta}>
          <Text style={[styles.metaText, { color: c.textSecondary }]}>
            {participantCount} competitors
          </Text>
          <Text style={[styles.metaText, { color: c.textSecondary }]}>
            Ends {new Date(season.ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Text>
        </View>

        {!registered ? (
          <TouchableOpacity
            style={[styles.registerBtn, { backgroundColor: c.accent }]}
            onPress={handleRegister}
            disabled={registering}
          >
            {registering ? (
              <ActivityIndicator size="small" color="#000" />
            ) : (
              <Text style={[styles.registerText, { fontFamily: TYPO.heading.bold }]}>
                JOIN SEASON
              </Text>
            )}
          </TouchableOpacity>
        ) : (
          <View style={[styles.registeredBadge, { borderColor: c.accent }]}>
            <Text style={[styles.registeredText, { color: c.accent, fontFamily: TYPO.body.semibold }]}>
              ✓ REGISTERED
            </Text>
          </View>
        )}
      </TouchableOpacity>

      {/* Weekly Challenge Card */}
      {challenge && (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => router.push('/challenge-leaderboard')}
          style={[styles.card, { backgroundColor: c.surface, borderColor: c.accent + '40' }]}
        >
          <View style={styles.challengeHeader}>
            <Flame size={18} color={c.accent} />
            <Text style={[styles.challengeLabel, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
              WEEKLY CHALLENGE
            </Text>
            <View style={{ flex: 1 }} />
            <Clock size={14} color={c.textTertiary} />
            <Text style={[styles.daysLeft, { color: c.textTertiary }]}>
              {daysLeft}d left · Ends {endDayName}
            </Text>
          </View>

          <Text style={[styles.challengeTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            {challenge.title}
          </Text>
          <Text style={[styles.challengeDesc, { color: c.textSecondary }]}>
            {challenge.description}
          </Text>

          <TouchableOpacity
            style={[styles.submitBtn, { borderColor: c.accent }]}
            onPress={() => setShowSubmitModal(true)}
          >
            <Text style={[styles.submitText, { color: c.accent, fontFamily: TYPO.body.semibold }]}>
              SUBMIT SCORE
            </Text>
            <ChevronRight size={16} color={c.accent} />
          </TouchableOpacity>
        </TouchableOpacity>
      )}

      {challenge && showSubmitModal && (
        <ChallengeSubmitModal
          visible={showSubmitModal}
          challenge={challenge}
          onClose={() => setShowSubmitModal(false)}
          onSubmitted={() => setShowSubmitModal(false)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
    paddingHorizontal: 16,
  },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  seasonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  seasonName: {
    fontSize: 15,
    letterSpacing: 1,
  },
  seasonMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metaText: {
    fontSize: 12,
  },
  registerBtn: {
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 2,
  },
  registerText: {
    fontSize: 14,
    color: '#000',
    letterSpacing: 1,
  },
  registeredBadge: {
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 2,
    borderWidth: 1,
  },
  registeredText: {
    fontSize: 13,
    letterSpacing: 1,
  },
  challengeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  challengeLabel: {
    fontSize: 11,
    letterSpacing: 1.5,
  },
  daysLeft: {
    fontSize: 12,
    marginLeft: 4,
  },
  challengeTitle: {
    fontSize: 18,
    marginBottom: 4,
  },
  challengeDesc: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: 2,
  },
  submitText: {
    fontSize: 13,
    letterSpacing: 1,
  },
});
